import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/store/tenantStore';
import {
  getHomeBannerImageUrl,
  getWhiteLogoUrl,
  getLogoImageSize,
  showOnBoardingLogo,
} from '@/services/tenant/tenantImages';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useLocalization } from '@/hooks/useLocalization';
import { translateDynamicContent } from '@/i18n';
import { ROUTES } from '@/app/router/routes';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import { AuthLogo } from './AuthLogo';
import styles from '../AuthPage.module.css';

/**
 * AuthLayout — shared frame for the auth screens (/login, /signUp, /mfa): the onboarding
 * banner, the auth logo, then the card(s). The app header is shown above it.
 *
 * The banner holds what Flutter's WebOnboardingPage panel shows beside these forms
 * (presentation/web/auth/web_onboarding/web_onboarding.dart), in the reference-UI layout:
 *   - background: the brand color unless the preference enableOnboardingBackgroundColor is false
 *   - the white logo (onBoardingPage.showLogo != false); tapping it goes home, or `onLogoClick`
 *   - onBoardingPage.headerText and footerText, translated from the language file's
 *     `dynamicContent` map (localizationDynamicContent.getValue)
 *   - the home page banner image, covering the banner (replaces Flutter's onboarding image)
 *
 * `single` centres one card (verify-email, MFA) instead of the two-column grid.
 */

interface AuthLayoutProps {
  children: ReactNode;
  single?: boolean;
  /** Logo tap — Flutter WebOnboardingPage.onLogout; defaults to going home */
  onLogoClick?: () => void;
}

export function AuthLayout({ children, single = false, onLogoClick }: AuthLayoutProps) {
  const navigate = useNavigate();
  const { content, preferences } = useTenantStore();
  useLocalization(); // re-render when the language file loads or the language changes

  const brandName = content?.contentSettings?.general?.name ?? '';
  const headerText = content?.contentSettings?.onBoardingPage?.headerText;
  const footerText = content?.contentSettings?.onBoardingPage?.footerText;
  const tagline = headerText ? translateDynamicContent(headerText) : undefined;
  const footer = footerText ? translateDynamicContent(footerText) : undefined;

  // The home page banner image fills the banner (redesign), instead of the onboarding image
  const imageUrl = getHomeBannerImageUrl(content, preferences as Record<string, unknown> | null);
  const logoUrl = showOnBoardingLogo(content) ? getWhiteLogoUrl(content) : undefined;
  const logoSize = getLogoImageSize(content);
  const brandBackground = (preferences as Record<string, unknown> | null)?.['enableOnboardingBackgroundColor'] !== false;

  function handleLogoClick() {
    if (onLogoClick) {
      onLogoClick();
      return;
    }
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }

  return (
    <div className={styles.page}>
      <AppHeader />
      <main className={styles.main}>
        <div className={`${styles.banner} ${brandBackground ? styles.bannerBrand : ''}`}>
          {imageUrl && (
            <div
              className={styles.bannerImage}
              style={{ backgroundImage: `url(${JSON.stringify(imageUrl)})` }}
              aria-hidden
            />
          )}
          {(logoUrl || tagline || footer) && (
            <div className={styles.bannerOverlay}>
              {logoUrl && (
                <button type="button" className={styles.bannerLogoBtn} onClick={handleLogoClick}>
                  <img src={logoUrl} alt={brandName} className={styles.bannerLogo} style={logoSize} />
                </button>
              )}
              {tagline && <div className={styles.bannerTagline}>{tagline}</div>}
              {footer && <div className={styles.bannerFooterText}>{footer}</div>}
            </div>
          )}
        </div>

        <AuthLogo />

        <div className={`${styles.grid} ${single ? styles.gridSingle : ''}`}>
          {children}
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
